import { useEffect, useState } from "react";
import { AppShell } from "@/components/Nav";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Switch } from "@/components/ui/switch";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from "@/components/ui/dialog";
import { useAuth } from "@/context/AuthContext";
import { Navigate } from "react-router-dom";
import api, { formatApiError } from "@/lib/api";
import { toast } from "sonner";
import { Plus, Edit, Trash2, Save, X, ShieldAlert, Users, Inbox } from "lucide-react";

const money = (cents) => `$${(cents / 100).toFixed(2)}`;

const EMPTY_FORM = {
  bank_name: "",
  account_type: "credit_card",
  credit_limit: "",
  age_months: "",
  utilization_pct: "",
  statement_date: "",
  price_cents: "",
  spots_available: "1",
  est_score_lift: "",
  posting_speed_days: "",
  is_active: true,
  notes: "",
};

function TradelineEditor({ open, initial, onClose, onSaved }) {
  const [form, setForm] = useState(EMPTY_FORM);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (open) {
      if (initial) {
        setForm({
          bank_name: initial.bank_name || "",
          account_type: initial.account_type || "credit_card",
          credit_limit: initial.credit_limit ?? "",
          age_months: initial.age_months ?? "",
          utilization_pct: initial.utilization_pct ?? "",
          statement_date: initial.statement_date || "",
          price_cents: initial.price_cents ?? "",
          spots_available: initial.spots_available ?? 1,
          est_score_lift: initial.est_score_lift || "",
          posting_speed_days: initial.posting_speed_days || "",
          is_active: initial.is_active ?? true,
          notes: initial.notes || "",
        });
      } else {
        setForm(EMPTY_FORM);
      }
    }
  }, [open, initial]);

  const save = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      const payload = {
        bank_name: form.bank_name,
        account_type: form.account_type,
        credit_limit: parseFloat(form.credit_limit) || 0,
        age_months: parseInt(form.age_months, 10) || 0,
        utilization_pct: form.utilization_pct === "" ? null : parseFloat(form.utilization_pct),
        statement_date: form.statement_date || null,
        price_cents: parseInt(form.price_cents, 10) || 0,
        spots_available: parseInt(form.spots_available, 10) || 0,
        est_score_lift: form.est_score_lift || null,
        posting_speed_days: form.posting_speed_days || null,
        is_active: form.is_active,
        notes: form.notes || null,
      };
      if (initial?.id) {
        await api.put(`/admin/tradelines/${initial.id}`, payload);
        toast.success("Updated");
      } else {
        await api.post("/admin/tradelines", payload);
        toast.success("Created");
      }
      onSaved();
    } catch (err) {
      toast.error(formatApiError(err));
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={(v) => !v && onClose()}>
      <DialogContent
        className="max-w-2xl bg-[#131518] border-[#26282C] text-[#F5F3EE]"
        data-testid="admin-tradeline-editor"
      >
        <DialogHeader>
          <DialogTitle className="font-display text-2xl">
            {initial ? "Edit tradeline" : "New tradeline"}
          </DialogTitle>
        </DialogHeader>
        <form onSubmit={save} className="space-y-3">
          <div className="grid md:grid-cols-2 gap-3">
            <div>
              <Label className="overline">Bank name</Label>
              <Input
                required
                value={form.bank_name}
                onChange={(e) => setForm({ ...form, bank_name: e.target.value })}
                data-testid="admin-tl-bank"
                className="mt-1 rounded-sm font-mono bg-[#0B0C0E] border-[#26282C]"
              />
            </div>
            <div>
              <Label className="overline">Type</Label>
              <select
                value={form.account_type}
                onChange={(e) => setForm({ ...form, account_type: e.target.value })}
                data-testid="admin-tl-type"
                className="mt-1 w-full rounded-sm bg-[#0B0C0E] border border-[#26282C] px-3 py-2 font-mono text-sm"
              >
                <option value="credit_card">Credit card</option>
                <option value="auto">Auto</option>
                <option value="mortgage">Mortgage</option>
                <option value="other">Other</option>
              </select>
            </div>
            <div>
              <Label className="overline">Credit limit ($)</Label>
              <Input
                required
                type="number"
                value={form.credit_limit}
                onChange={(e) => setForm({ ...form, credit_limit: e.target.value })}
                data-testid="admin-tl-limit"
                className="mt-1 rounded-sm font-mono bg-[#0B0C0E] border-[#26282C]"
              />
            </div>
            <div>
              <Label className="overline">Age (months)</Label>
              <Input
                required
                type="number"
                value={form.age_months}
                onChange={(e) => setForm({ ...form, age_months: e.target.value })}
                data-testid="admin-tl-age"
                className="mt-1 rounded-sm font-mono bg-[#0B0C0E] border-[#26282C]"
              />
            </div>
            <div>
              <Label className="overline">Utilization %</Label>
              <Input
                type="number"
                value={form.utilization_pct}
                onChange={(e) => setForm({ ...form, utilization_pct: e.target.value })}
                data-testid="admin-tl-util"
                className="mt-1 rounded-sm font-mono bg-[#0B0C0E] border-[#26282C]"
              />
            </div>
            <div>
              <Label className="overline">Statement day</Label>
              <Input
                value={form.statement_date}
                onChange={(e) => setForm({ ...form, statement_date: e.target.value })}
                data-testid="admin-tl-statement"
                className="mt-1 rounded-sm font-mono bg-[#0B0C0E] border-[#26282C]"
                placeholder="12"
              />
            </div>
            <div>
              <Label className="overline">Price (cents)</Label>
              <Input
                required
                type="number"
                value={form.price_cents}
                onChange={(e) => setForm({ ...form, price_cents: e.target.value })}
                data-testid="admin-tl-price"
                className="mt-1 rounded-sm font-mono bg-[#0B0C0E] border-[#26282C]"
                placeholder="18500"
              />
            </div>
            <div>
              <Label className="overline">Spots available</Label>
              <Input
                required
                type="number"
                value={form.spots_available}
                onChange={(e) => setForm({ ...form, spots_available: e.target.value })}
                data-testid="admin-tl-spots"
                className="mt-1 rounded-sm font-mono bg-[#0B0C0E] border-[#26282C]"
              />
            </div>
            <div>
              <Label className="overline">Est. score lift</Label>
              <Input
                value={form.est_score_lift}
                onChange={(e) => setForm({ ...form, est_score_lift: e.target.value })}
                data-testid="admin-tl-lift"
                className="mt-1 rounded-sm font-mono bg-[#0B0C0E] border-[#26282C]"
                placeholder="30-70 pts"
              />
            </div>
            <div>
              <Label className="overline">Posting speed</Label>
              <Input
                value={form.posting_speed_days}
                onChange={(e) => setForm({ ...form, posting_speed_days: e.target.value })}
                data-testid="admin-tl-speed"
                className="mt-1 rounded-sm font-mono bg-[#0B0C0E] border-[#26282C]"
                placeholder="10-25 days"
              />
            </div>
          </div>
          <div>
            <Label className="overline">Notes</Label>
            <Textarea
              value={form.notes}
              onChange={(e) => setForm({ ...form, notes: e.target.value })}
              data-testid="admin-tl-notes"
              className="mt-1 rounded-sm bg-[#0B0C0E] border-[#26282C]"
              rows={3}
            />
          </div>
          <div className="flex items-center gap-3">
            <Switch
              checked={form.is_active}
              onCheckedChange={(v) => setForm({ ...form, is_active: v })}
              data-testid="admin-tl-active"
            />
            <span className="text-sm text-[#A8ADA5]">Listing is active (visible to users)</span>
          </div>
          <DialogFooter className="pt-2">
            <Button type="button" onClick={onClose} className="rounded-full btn-outline">
              <X size={14} className="mr-1" /> Cancel
            </Button>
            <Button
              type="submit"
              disabled={saving}
              data-testid="admin-tl-save"
              className="rounded-full btn-lime"
            >
              <Save size={14} className="mr-1" /> {saving ? "Saving…" : "Save"}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}

export default function AdminTradeLines() {
  const { user, loading: authLoading } = useAuth();
  const [tab, setTab] = useState("listings");
  const [items, setItems] = useState([]);
  const [orders, setOrders] = useState([]);
  const [leads, setLeads] = useState([]);
  const [loading, setLoading] = useState(true);
  const [editing, setEditing] = useState(null);
  const [showEditor, setShowEditor] = useState(false);

  const reload = async () => {
    setLoading(true);
    try {
      const [tl, ord, ld] = await Promise.all([
        api.get("/admin/tradelines"),
        api.get("/admin/tradeline-orders"),
        api.get("/admin/tradeline-leads"),
      ]);
      setItems(tl.data || []);
      setOrders(ord.data || []);
      setLeads(ld.data || []);
    } catch (err) {
      toast.error(formatApiError(err));
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (user?.is_admin) reload();
  }, [user?.is_admin]);

  if (authLoading) return null;
  if (!user?.is_admin) {
    return <Navigate to="/app" replace />;
  }

  const remove = async (tl) => {
    if (!window.confirm(`Delete "${tl.bank_name}"?`)) return;
    try {
      await api.delete(`/admin/tradelines/${tl.id}`);
      toast.success("Deleted");
      reload();
    } catch (err) {
      toast.error(formatApiError(err));
    }
  };

  const updateOrderStatus = async (o, status) => {
    try {
      await api.patch(`/admin/tradeline-orders/${o.id}`, { fulfillment_status: status });
      toast.success("Status updated");
      reload();
    } catch (err) {
      toast.error(formatApiError(err));
    }
  };

  return (
    <AppShell>
      <div className="mb-8 flex items-start justify-between gap-4">
        <div>
          <div className="overline mb-3 text-[#D5FF3F]">Admin</div>
          <h1 className="font-display text-5xl md:text-6xl leading-none">Tradelines console.</h1>
          <p className="text-[#A8ADA5] mt-2 max-w-2xl">
            Manage inventory, fulfill orders, and review concierge leads.
          </p>
        </div>
        {tab === "listings" && (
          <Button
            onClick={() => {
              setEditing(null);
              setShowEditor(true);
            }}
            data-testid="admin-new-tradeline-btn"
            className="rounded-full btn-lime h-11 px-5"
          >
            <Plus size={15} className="mr-1.5" /> New listing
          </Button>
        )}
      </div>

      <div className="mb-6 inline-flex items-center gap-1 p-1 border border-[#26282C] rounded-full bg-[#131518]">
        {[
          { k: "listings", l: "Listings", i: ShieldAlert },
          { k: "orders", l: "Orders", i: Users },
          { k: "leads", l: "Leads", i: Inbox },
        ].map((t) => {
          const Icon = t.i;
          return (
            <button
              key={t.k}
              onClick={() => setTab(t.k)}
              data-testid={`admin-tab-${t.k}`}
              className={`px-4 py-1.5 rounded-full text-sm font-mono uppercase tracking-widest transition-colors flex items-center gap-2 ${
                tab === t.k ? "bg-[#D5FF3F] text-[#0B0C0E]" : "text-[#A8ADA5] hover:text-[#F5F3EE]"
              }`}
            >
              <Icon size={13} /> {t.l}
            </button>
          );
        })}
      </div>

      {loading ? (
        <div className="text-[#A8ADA5]">Loading…</div>
      ) : tab === "listings" ? (
        <div className="card-surface overflow-x-auto" data-testid="admin-listings-table">
          <table className="w-full text-sm">
            <thead className="text-left border-b border-[#26282C]">
              <tr>
                {["Bank", "Limit", "Age", "Price", "Spots", "Active", ""].map((h) => (
                  <th key={h} className="px-4 py-3 overline">
                    {h}
                  </th>
                ))}
              </tr>
            </thead>
            <tbody>
              {items.length === 0 ? (
                <tr>
                  <td colSpan={7} className="px-4 py-8 text-center text-[#A8ADA5]">
                    No listings yet.
                  </td>
                </tr>
              ) : (
                items.map((tl) => (
                  <tr key={tl.id} className="border-b border-[#26282C]/50">
                    <td className="px-4 py-3 font-medium">{tl.bank_name}</td>
                    <td className="px-4 py-3 font-mono">${tl.credit_limit?.toLocaleString()}</td>
                    <td className="px-4 py-3 font-mono">
                      {Math.floor((tl.age_months || 0) / 12)}y {(tl.age_months || 0) % 12}m
                    </td>
                    <td className="px-4 py-3 font-mono">{money(tl.price_cents)}</td>
                    <td className="px-4 py-3 font-mono">{tl.spots_available}</td>
                    <td className="px-4 py-3">
                      <span
                        className={`text-[10px] px-2 py-0.5 rounded-full font-mono uppercase tracking-widest ${
                          tl.is_active
                            ? "bg-[#D5FF3F] text-[#0B0C0E]"
                            : "bg-[#17191C] text-[#6D716C]"
                        }`}
                      >
                        {tl.is_active ? "Live" : "Draft"}
                      </span>
                    </td>
                    <td className="px-4 py-3 text-right whitespace-nowrap">
                      <Button
                        onClick={() => {
                          setEditing(tl);
                          setShowEditor(true);
                        }}
                        data-testid={`admin-edit-tl-${tl.id}`}
                        className="rounded-full btn-outline h-8 px-3 mr-1"
                      >
                        <Edit size={12} />
                      </Button>
                      <Button
                        onClick={() => remove(tl)}
                        data-testid={`admin-del-tl-${tl.id}`}
                        className="rounded-full btn-outline h-8 px-3 hover:!text-[#FF6B4A]"
                      >
                        <Trash2 size={12} />
                      </Button>
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>
      ) : tab === "orders" ? (
        <div className="space-y-3" data-testid="admin-orders-list">
          {orders.length === 0 ? (
            <div className="card-surface p-8 text-center text-[#A8ADA5]">No orders yet.</div>
          ) : (
            orders.map((o) => (
              <div key={o.id} className="card-surface p-5">
                <div className="flex items-start justify-between gap-4 flex-wrap mb-3">
                  <div>
                    <div className="font-medium">{o.tradeline_snapshot?.bank_name}</div>
                    <div className="text-xs text-[#6D716C] font-mono">
                      {new Date(o.created_at).toLocaleString()} ·{" "}
                      {money(o.tradeline_snapshot?.price_cents || 0)}
                    </div>
                  </div>
                  <div className="flex items-center gap-2">
                    <span className="text-[10px] px-2 py-0.5 rounded-full font-mono uppercase tracking-widest bg-[#17191C] text-[#A8ADA5]">
                      Pay · {o.payment_status}
                    </span>
                    <select
                      value={o.fulfillment_status}
                      onChange={(e) => updateOrderStatus(o, e.target.value)}
                      data-testid={`admin-order-status-${o.id}`}
                      className="text-xs rounded-sm bg-[#0B0C0E] border border-[#26282C] px-2 py-1 font-mono"
                    >
                      {["pending", "processing", "posted", "delivered", "refunded", "cancelled"].map((s) => (
                        <option key={s}>{s}</option>
                      ))}
                    </select>
                  </div>
                </div>
                <div className="grid md:grid-cols-2 gap-4 text-sm">
                  <div>
                    <div className="overline mb-1">AU contact</div>
                    <div className="text-[#F5F3EE]">{o.contact?.full_name}</div>
                    <div className="text-xs text-[#A8ADA5] font-mono">
                      DOB {o.contact?.date_of_birth} · SSN ****{o.contact?.ssn_last_4}
                    </div>
                    <div className="text-xs text-[#A8ADA5] font-mono">{o.contact?.phone}</div>
                  </div>
                  <div>
                    <div className="overline mb-1">Mailing</div>
                    <div className="text-sm text-[#F5F3EE]">
                      {o.contact?.address?.line1}
                      {o.contact?.address?.line2 && <>, {o.contact.address.line2}</>}
                    </div>
                    <div className="text-sm text-[#F5F3EE] font-mono">
                      {o.contact?.address?.city}, {o.contact?.address?.state}{" "}
                      {o.contact?.address?.zip}
                    </div>
                  </div>
                </div>
                {o.contact?.notes && (
                  <div className="mt-3 text-xs text-[#A8ADA5] italic">{o.contact.notes}</div>
                )}
              </div>
            ))
          )}
        </div>
      ) : (
        <div className="space-y-3" data-testid="admin-leads-list">
          {leads.length === 0 ? (
            <div className="card-surface p-8 text-center text-[#A8ADA5]">No leads yet.</div>
          ) : (
            leads.map((l) => (
              <div key={l.id} className="card-surface p-5">
                <div className="flex items-start justify-between gap-3 mb-2">
                  <div>
                    <div className="font-medium">{l.user_name || l.user_email}</div>
                    <div className="text-xs text-[#6D716C] font-mono">
                      {new Date(l.created_at).toLocaleString()} · {l.user_email}
                    </div>
                  </div>
                  <span className="text-[10px] px-2 py-0.5 rounded-full font-mono uppercase tracking-widest bg-[#D5FF3F]/15 text-[#D5FF3F]">
                    {l.goal}
                  </span>
                </div>
                <div className="grid grid-cols-3 gap-3 text-xs font-mono text-[#A8ADA5] mb-2">
                  {l.target_score && <div>Target: {l.target_score}</div>}
                  {l.budget_cents && <div>Budget: ${(l.budget_cents / 100).toFixed(0)}</div>}
                  {l.contact_phone && <div>Phone: {l.contact_phone}</div>}
                </div>
                {l.notes && <div className="text-sm text-[#A8ADA5]">{l.notes}</div>}
              </div>
            ))
          )}
        </div>
      )}

      <TradelineEditor
        open={showEditor}
        initial={editing}
        onClose={() => setShowEditor(false)}
        onSaved={() => {
          setShowEditor(false);
          reload();
        }}
      />
    </AppShell>
  );
}
